import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { Title, useCanAccess, useTranslate } from "react-admin";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { CrmCard } from "../components/crm/CrmCard";
import { CrmPageHeader } from "../components/crm/CrmPageHeader";
import { RequirePermission } from "../components/RequirePermission";
import { SalesforceReportDashboardContent } from "../components/SalesforceReportDashboardContent";
import { LOGISTICS_INVENTORY_ROUTE, ResourceName } from "../constants/resources";
import { CrmObject, SalesforceReportDashboard } from "../generated/graphql";
import { CRM_COLORS, crmListPath } from "../lib/crm";

const LogisticsContent = () => {
  const translate = useTranslate();
  const navigate = useNavigate();
  const { canAccess: canOpenCrm, isPending: crmPending } = useCanAccess({ action: "list", resource: ResourceName.Crm });
  const { canAccess: canOpenReports, isPending: reportsPending } = useCanAccess({
    action: "list",
    resource: ResourceName.LogisticsReports,
  });
  const showCrm = canOpenCrm === true && !crmPending;
  const [inventorySearch, setInventorySearch] = useState("");
  const [returnSearch, setReturnSearch] = useState("");

  return (
    <Box sx={{ p: "16px", backgroundColor: CRM_COLORS.pageBackground, minHeight: "100%" }}>
      <Title title={translate("logistics.overview.pageTitle")} />
      <CrmPageHeader
        icon={<Inventory2OutlinedIcon sx={{ color: CRM_COLORS.brand, fontSize: 32 }} />}
        overline={translate("logistics.section")}
        title={translate("logistics.overview.title")}
        meta={translate("logistics.overview.description")}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: showCrm ? "repeat(3, 1fr)" : "1fr" },
          gap: "12px",
          alignItems: "start",
        }}
      >
        {showCrm && (
          <CrmCard title={translate("logistics.overview.shipping.title")}>
            <Typography sx={{ mb: "16px", fontSize: "13px", color: CRM_COLORS.textWeak }}>
              {translate("logistics.overview.shipping.description")}
            </Typography>
            <Button
              component={RouterLink}
              to={crmListPath(CrmObject.Order, "readyForShipping")}
              variant="contained"
              size="small"
            >
              {translate("logistics.overview.shipping.open")}
            </Button>
          </CrmCard>
        )}
        <CrmCard title={translate("logistics.overview.inventory.title")}>
          <Typography sx={{ mb: "16px", fontSize: "13px", color: CRM_COLORS.textWeak }}>
            {translate("logistics.overview.inventory.description")}
          </Typography>
          <Box
            component="form"
            aria-label={translate("logistics.overview.inventory.title")}
            onSubmit={event => {
              event.preventDefault();
              const term = inventorySearch.trim();
              if (term) navigate(`${LOGISTICS_INVENTORY_ROUTE}?${new URLSearchParams({ q: term })}`);
            }}
            sx={{ display: "grid", gap: "12px" }}
          >
            <TextField
              label={translate("logistics.overview.inventory.search")}
              value={inventorySearch}
              onChange={event => setInventorySearch(event.target.value)}
              size="small"
              fullWidth
            />
            <Button type="submit" variant="contained" size="small" disabled={!inventorySearch.trim()}>
              {translate("logistics.overview.inventory.submit")}
            </Button>
            <Button component={RouterLink} to={LOGISTICS_INVENTORY_ROUTE} size="small">
              {translate("logistics.overview.inventory.open")}
            </Button>
          </Box>
        </CrmCard>
        {showCrm && (
          <CrmCard title={translate("logistics.overview.returns.title")}>
            <Typography sx={{ mb: "16px", fontSize: "13px", color: CRM_COLORS.textWeak }}>
              {translate("logistics.overview.returns.description")}
            </Typography>
            <Box
              component="form"
              aria-label={translate("logistics.overview.returns.title")}
              onSubmit={event => {
                event.preventDefault();
                const term = returnSearch.trim();
                if (term)
                  navigate(`${crmListPath(CrmObject.Order, "returnLookup")}&${new URLSearchParams({ q: term })}`);
              }}
              sx={{ display: "grid", gap: "12px" }}
            >
              <TextField
                label={translate("logistics.overview.returns.search")}
                value={returnSearch}
                onChange={event => setReturnSearch(event.target.value)}
                size="small"
                fullWidth
              />
              <Button type="submit" variant="contained" size="small" disabled={!returnSearch.trim()}>
                {translate("logistics.overview.returns.submit")}
              </Button>
            </Box>
          </CrmCard>
        )}
      </Box>
      {canOpenReports === true && !reportsPending && (
        <SalesforceReportDashboardContent
          dashboard={SalesforceReportDashboard.LogisticsOperations}
          slug="logistics"
          context="logistics"
          embedded
        />
      )}
    </Box>
  );
};

export const LogisticsPage = () => (
  <RequirePermission permission={ResourceName.LogisticsInventory}>
    <LogisticsContent />
  </RequirePermission>
);
